import {
  Box,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState, useCallback } from "react";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { updateProfile } from "../../actions/userAction";
import ApiRequest from "../../actions/ApiRequest";
import Toast from "components/common/Toast";
import { set } from "lodash";
import translate from "i18n/translate";
const useStyles = makeStyles((theme) => ({
  textField: {
    width: 200,
    marginRight: 1,
  },
  button: {
    marginLeft: 1,
  },
}));
const ChangeProfile = ({
  desc,
  variable,
  userDetails,
  aKey,
  checkbox = false,
  disabled = false,
  special = false,
  enabled,
  getSellerData,
  selectOptions = null,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [originalValue, setOriginalValue] = useState("");
  const [changeText, setChangeText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const sendApiRequest = useCallback(async (userDetailsToUpdate) => {
    try {
      // URL-encode the value to handle special characters (e.g., / in /pay_bills)
      const encodedValue = encodeURIComponent(userDetailsToUpdate[aKey]);
      const url = `update_seller_profile?operation=profile&${aKey}=${encodedValue}&seller_id=${userDetailsToUpdate.id}`;
      
      console.log(`🔄 Updating ${aKey} to:`, userDetailsToUpdate[aKey]);
      console.log(`📡 API URL:`, url);
      
      const res = await ApiRequest.post(url);
      
      console.log(`📥 API Response:`, res.data);
      
      if (res.data.status && res.data.status.toLowerCase() === "failed") {
        throw new Error(res.data.reason || "Update failed");
      }
      
      await getSellerData();
      setOriginalValue(changeText);
      setIsChecked((prev) => !prev);
      Toast.fire({
        title: translate("Settings Updated Successfully!"),
        icon: "success",
      });
    } catch (err) {
      console.error(`❌ Error updating ${aKey}:`, err);
      Toast.fire({
        title: err.message || "Something went wrong",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [aKey, getSellerData, changeText]);

  const handleCheckboxChange = useCallback((event) => {
    setIsLoading(true);
    sendApiRequest({ ...userDetails, [aKey]: event.target.checked });
  }, [userDetails, aKey, sendApiRequest]);

  useEffect(() => {
    // Ensure variable is converted to string to prevent [object Object] display
    const stringValue = typeof variable === 'object' && variable !== null 
      ? JSON.stringify(variable) 
      : String(variable || '');
    
    setOriginalValue(stringValue);
    setChangeText(stringValue);
    setIsChecked(variable === "true" || variable === true ? true : false);
  }, [variable]);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    setChangeText(originalValue);
  }, [originalValue]);

  const handleSaveClick = useCallback(() => {
    setIsEditing(false);
    if (changeText === originalValue || changeText.trim().length === 0) return;
    const updatedDetails = { ...userDetails, [aKey]: changeText };
    setIsLoading(true);
    sendApiRequest(updatedDetails);
  }, [changeText, originalValue, userDetails, aKey, sendApiRequest]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleChange = useCallback((event) => {
    setChangeText(event.target.value);
  }, []);

  return (
    <Box className="profile-field-modern" sx={{ position: "relative" }}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: "50%",
            padding: "8px"
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {checkbox ? (
        <Box sx={{ padding: 0 }}>
          <FormControl 
            component="fieldset"
            disabled={disabled}
            sx={{ width: '100%' }}
          >
            <FormLabel 
              component="legend"
              sx={{
                fontWeight: 600,
                fontSize: '11px',
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
                '&.Mui-focused': {
                  color: '#4a5568',
                }
              }}
            >
              {desc}
            </FormLabel>
            <RadioGroup
              row
              value={isChecked ? 'on' : 'off'}
              onChange={(e) => {
                const newValue = e.target.value === 'on';
                setIsChecked(newValue);
                handleCheckboxChange({ target: { checked: newValue } });
              }}
              sx={{ gap: 1.5 }}
            >
              <FormControlLabel
                value="on"
                control={
                  <Radio 
                    sx={{
                      color: '#4a5568',
                      '&.Mui-checked': {
                        color: '#10b981',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: '16px', color: '#10b981' }} />
                    <Box sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {translate("ON")}
                    </Box>
                  </Box>
                }
                sx={{
                  padding: '6px 12px',
                  margin: 0,
                  borderRadius: '6px',
                  border: isChecked 
                    ? '2px solid #10b981' 
                    : '2px solid #e2e8f0',
                  backgroundColor: isChecked 
                    ? 'rgba(16, 185, 129, 0.1)' 
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isChecked 
                      ? 'rgba(16, 185, 129, 0.15)' 
                      : 'rgba(102, 126, 234, 0.05)',
                  }
                }}
              />
              <FormControlLabel
                value="off"
                control={
                  <Radio 
                    sx={{
                      color: '#4a5568',
                      '&.Mui-checked': {
                        color: '#ef4444',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CancelIcon sx={{ fontSize: '16px', color: '#ef4444' }} />
                    <Box sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {translate("OFF")}
                    </Box>
                  </Box>
                }
                sx={{
                  padding: '6px 12px',
                  margin: 0,
                  borderRadius: '6px',
                  border: !isChecked 
                    ? '2px solid #ef4444' 
                    : '2px solid #e2e8f0',
                  backgroundColor: !isChecked 
                    ? 'rgba(239, 68, 68, 0.1)' 
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: !isChecked 
                      ? 'rgba(239, 68, 68, 0.15)' 
                      : 'rgba(102, 126, 234, 0.05)',
                  }
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      ) : (
        <Box sx={{ opacity: isLoading ? 0.5 : 1, transition: "opacity 0.3s" }}>
          {isEditing ? (
            <Box>
              <label className="field-label-modern">{desc}</label>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                {selectOptions ? (
                  <Select
                    value={changeText}
                    onChange={(e) => setChangeText(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      flex: 1,
                      minWidth: "150px",
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      fontSize: '14px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    }}
                  >
                    {selectOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField
                    value={changeText}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      flex: 1,
                      minWidth: "150px",
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        '& input': {
                          padding: '8px 12px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                )}
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveClick}
                    size="small"
                    startIcon={<SaveIcon fontSize="small" />}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '6px 12px',
                      minWidth: 'auto',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      },
                    }}
                  >
                    {translate("save")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancelClick}
                    size="small"
                    startIcon={<CancelIcon fontSize="small" />}
                    color="error"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '6px 12px',
                      minWidth: 'auto',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    {translate("cancel")}
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <label className="field-label-modern">{desc}</label>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
                <Box
                  className="field-value-modern"
                  sx={{
                    flex: 1,
                    color: special ? (enabled ? "#10b981" : "#ef4444") : "inherit",
                    fontWeight: special ? 600 : 500,
                    padding: special ? "6px 10px" : "0",
                    backgroundColor: special ? (enabled ? "#f0fdf4" : "#fef2f2") : "transparent",
                    borderRadius: "6px",
                    border: special ? (enabled ? "2px solid #d1fae5" : "2px solid #fee2e2") : "none",
                  }}
                >
                  {aKey === "password" || aKey === "passw" 
                    ? "••••••••••" 
                    : (typeof originalValue === 'object' && originalValue !== null 
                        ? JSON.stringify(originalValue) 
                        : String(originalValue || "-"))}
                </Box>
                {!disabled && (
                  <IconButton
                    onClick={handleEditClick}
                    size="small"
                    sx={{
                      ml: 1,
                      padding: '6px',
                      color: '#667eea',
                      backgroundColor: '#f0f4ff',
                      '&:hover': {
                        backgroundColor: '#e0e7ff',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <EditIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChangeProfile;
